// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Layer table and layer-set resolution, including the `*.Cu` style wildcards
 * that appear on pads (spec §6.2: "resolved layer list; expand `*.Cu` wildcards").
 */

import { atoms, get, getAll, isNode, type Node } from '../sexp/index.ts';

export interface LayerDef {
  ordinal: number;
  /** Canonical name, e.g. `F.Cu`. This is what object `(layer …)` fields use. */
  name: string;
  /** `signal`, `power`, `user`, `mixed`, `jumper`. */
  type: string;
  /** Optional user-visible alias, e.g. `F.Silkscreen`. */
  userName?: string;
}

export class LayerTable {
  readonly layers: LayerDef[];
  private readonly byName: Map<string, LayerDef>;

  constructor(layers: LayerDef[]) {
    this.layers = layers;
    this.byName = new Map(layers.map((l) => [l.name, l]));
  }

  static fromBoard(root: Node): LayerTable {
    const block = get(root, 'layers');
    const out: LayerDef[] = [];
    if (block) {
      for (const child of block.children) {
        if (!isNode(child)) continue;
        // `(0 "F.Cu" signal)` — the ordinal is the node *name*.
        const ordinal = Number(child.name);
        const vals = atoms(child).map((a) => a.value);
        const name = vals[0];
        if (!Number.isFinite(ordinal) || name === undefined) continue;
        const def: LayerDef = { ordinal, name, type: vals[1] ?? 'user' };
        if (vals[2] !== undefined) def.userName = vals[2];
        out.push(def);
      }
    }
    return new LayerTable(out);
  }

  has(name: string): boolean {
    return this.byName.has(name);
  }

  get names(): string[] {
    return this.layers.map((l) => l.name);
  }

  get copperNames(): string[] {
    return this.layers.filter((l) => l.name.endsWith('.Cu')).map((l) => l.name);
  }

  /**
   * Expand one layer token to concrete layer names.
   *
   * - `*.Cu` → every copper layer in this board's stack
   * - `*.Mask`, `*.Paste`, `*.SilkS`, … → every layer with that suffix
   * - `F&B.Cu` → `F.Cu`, `B.Cu`
   * - anything else → itself (unknown names are kept, not dropped)
   */
  expand(token: string): string[] {
    if (token.startsWith('*.')) {
      const suffix = token.slice(1); // ".Cu"
      const matches = this.layers.filter((l) => l.name.endsWith(suffix)).map((l) => l.name);
      return matches.length > 0 ? matches : [token];
    }
    if (token.startsWith('F&B.')) {
      const suffix = token.slice(3); // ".Cu"
      return [`F${suffix}`, `B${suffix}`];
    }
    return [token];
  }

  expandAll(tokens: readonly string[]): string[] {
    const out = new Set<string>();
    for (const t of tokens) for (const n of this.expand(t)) out.add(n);
    return [...out];
  }
}

/**
 * Resolve an object's layer list from whichever spelling it uses:
 * `(layer "F.Cu")`, `(layers "F.Cu" "B.Cu")`, or both (zones in KiCad 9 may
 * carry `(layers …)` for multi-layer zones).
 */
export function resolveLayers(n: Node, table: LayerTable): string[] {
  const tokens: string[] = [];
  for (const single of getAll(n, 'layer')) {
    for (const a of atoms(single)) tokens.push(a.value);
  }
  for (const multi of getAll(n, 'layers')) {
    for (const a of atoms(multi)) tokens.push(a.value);
  }
  return table.expandAll(tokens);
}
